import { z } from 'zod';
import { IsoTimestamp, RunId, UserId } from './common';

/**
 * Forward-compat sentinel (#482). Every enum the mobile READS from the
 * wire ships a `*Readable` variant that folds unknown values into
 * `'unknown'` via `.catch()`. Server writes still use the strict enum
 * export so DDB never sees 'unknown' from a well-behaved worker.
 */
const UNKNOWN_ENUM_SENTINEL = 'unknown' as const;

const RUN_STATUS_VALUES = [
  'queued',
  'starting',
  'running',
  'done',
  'needs_attention',
  'failed',
  // User clicked Stop in the flyout / mobile. Worker finishes the
  // current application gracefully, then exits without picking up the
  // next job. Distinct from 'done' so the summary can say "stopped"
  // rather than "completed".
  'cancelled',
  // Daily cloud-LLM budget is exhausted. Worker halts the cycle and
  // surfaces the cap in the run summary. Resets at next UTC midnight.
  'paused_budget',
  // Per-tier wall-clock cap (TIER_CAPS[tier].maxRunMinutes) reached. Worker
  // finishes the in-flight Easy-Apply and exits at the next iteration.
  // Distinct from 'done' so the summary can say "time limit reached" rather
  // than implying the feed was exhausted.
  'time_cap_reached',
] as const;
export const RunStatus = z.enum(RUN_STATUS_VALUES);
export type RunStatus = z.infer<typeof RunStatus>;

/** Forward-compat read variant of `RunStatus` — see #482. */
export const RunStatusReadable = z
  .enum([...RUN_STATUS_VALUES, UNKNOWN_ENUM_SENTINEL] as const)
  .catch(UNKNOWN_ENUM_SENTINEL);
export type RunStatusReadable = z.infer<typeof RunStatusReadable>;

const RUN_ACTION_VALUES = [
  'idle',
  'scraping_feed',
  'evaluating_job',
  'applying_easy',
  'applying_external',
  'finalizing',
  // Daily applications-cap reached — the cycle stops here and the user
  // is told they're out for the day. Distinct from 'finalizing' so the
  // mobile/desktop UI can show a friendlier "back tomorrow" hint.
  'daily_cap_reached',
  // Worker is in the LLM-guided recovery loop on a stuck form. Distinct
  // from 'applying_*' so the flyout can show "Fixing form issue" instead
  // of leaving the label at "Submitting…" and then snapping straight to
  // "failed" — that gap made it look like the 1046-strategy learnings
  // corpus never fired (it did, just silently).
  'recovering_form',
  // Path-A recovery emitted a `needs-attention` action — the cloud
  // route detected a wall the worker can't get past (recaptcha v2,
  // identity-step modal, etc.). The flyout shows "Needs your attention"
  // and the cycle moves on without burning another submit.
  'needs_attention',
] as const;
export const RunAction = z.enum(RUN_ACTION_VALUES);
export type RunAction = z.infer<typeof RunAction>;

/** Forward-compat read variant of `RunAction` — see #482. */
export const RunActionReadable = z
  .enum([...RUN_ACTION_VALUES, UNKNOWN_ENUM_SENTINEL] as const)
  .catch(UNKNOWN_ENUM_SENTINEL);
export type RunActionReadable = z.infer<typeof RunActionReadable>;

export const RunCounters = z.object({
  jobsReviewed: z.number().int().nonnegative().default(0),
  jobsFiltered: z.number().int().nonnegative().default(0),
  jobsApplied: z.number().int().nonnegative().default(0),
  jobsSkipped: z.number().int().nonnegative().default(0),
  jobsFailed: z.number().int().nonnegative().default(0),
  needsAttention: z.number().int().nonnegative().default(0),
  // Applications the worker started but couldn't finish autonomously
  // (no answer-bank match for a hard question, mid-form LLM 429, etc.)
  // The run summary lists these with deep links so the user can finish
  // them by hand in their browser.
  needsCompletion: z.number().int().nonnegative().default(0),
  // External-apply attempts where the ATS host couldn't be recognized.
  // Drives the amber chip on the admin RunsList and the
  // /improvements/unknown-ats prioritization queue. Has a Zod default so
  // pre-existing run rows decode cleanly without a migration.
  externalAppliesUnknown: z.number().int().nonnegative().default(0),
  // Successful external applies where LinkedIn's "Did you apply?" prompt
  // was answered Yes — i.e., the card is now marked Applied on LinkedIn's
  // side rather than continuing to re-surface in the feed. Closes the
  // observability gap from #322 (the fix that re-introduced the
  // bringToFront before the prompt poll); see #328. Has a Zod default so
  // pre-#328 run rows decode cleanly without a migration.
  externalAppliesConfirmedOnLinkedIn: z.number().int().nonnegative().default(0),
  /**
   * Jobs the worker tried to apply to but couldn't complete, where it
   * then clicked LinkedIn's native "Save" button so the user can finish
   * the application by hand later. Gated by
   * `Preferences.saveJobOnApplyFail` (default on). Has a Zod default so
   * pre-existing run rows decode cleanly without a migration.
   */
  jobsSavedOnFail: z.number().int().nonnegative().default(0),
  /**
   * Cards the worker clicked LinkedIn's X on so the same job stops
   * resurfacing in the recommended feed every cycle. Fires at three
   * sites in `run-cycle.ts`:
   *   - precheck-already-applied (LinkedIn marks the card "Applied" but
   *     keeps surfacing it)
   *   - cross-run-dedup (`processedJobIds` set loaded at cycle start
   *     already saw this `linkedinJobId`)
   *   - prefilter-reject (`passesPreFilters` returned false)
   * Pre-#756 these were folded into `jobsSkipped` via a subtraction
   * (reviewed - applied - failed), making dismiss volume invisible —
   * which let a 77% badge-detection regression live in prod for 4
   * months (#755). Now its own bucket; `jobsSkipped` subtracts it so
   * the two don't overlap. Has a Zod default so pre-#756 run rows
   * decode cleanly without a migration.
   */
  jobsDismissed: z.number().int().nonnegative().default(0),
});
export type RunCounters = z.infer<typeof RunCounters>;

/**
 * Possible `triggeredBy` values on a Run record.
 *
 *  - `manual_mobile` / `manual_desktop` — produced by new code (#332
 *    deprecated the auto-fire scheduler; every cycle is now user-
 *    triggered).
 *  - `schedule` — retained as a valid READ value for historical RUN#
 *    rows written before #332 by the deleted Scheduler module. NOT
 *    accepted by the write boundary (`StartRunRequest`) — new writes
 *    must use one of the manual_* values.
 */
const RUN_TRIGGERED_BY_VALUES = ['schedule', 'manual_mobile', 'manual_desktop'] as const;
const RunTriggeredByReadable = z
  .enum([...RUN_TRIGGERED_BY_VALUES, UNKNOWN_ENUM_SENTINEL] as const)
  .catch(UNKNOWN_ENUM_SENTINEL);

export const Run = z.object({
  userId: UserId,
  runId: RunId,
  status: RunStatusReadable,
  triggeredBy: RunTriggeredByReadable,
  startedAt: IsoTimestamp,
  completedAt: IsoTimestamp.optional(),
  counters: RunCounters,
  errors: z.array(z.string()).default([]),
});

export type Run = z.infer<typeof Run>;

export const RunProgress = z.object({
  userId: UserId,
  runId: RunId,
  state: RunStatusReadable,
  action: RunActionReadable,
  currentJob: z
    .object({
      title: z.string(),
      company: z.string(),
      jobId: z.string().optional(),
    })
    .optional(),
  counters: RunCounters,
  updatedAt: IsoTimestamp,
});

export type RunProgress = z.infer<typeof RunProgress>;

/**
 * Write-boundary subset of `RunTriggeredBy`. Deliberately narrower —
 * `'schedule'` was retained on the read schema for historical rows
 * but the write API rejects it; every new cycle must declare its
 * user-trigger origin. See #332.
 */
export const StartRunRequest = z.object({
  triggeredBy: z.enum(['manual_mobile', 'manual_desktop']),
});
export type StartRunRequest = z.infer<typeof StartRunRequest>;

export const ProgressUpdateRequest = z.object({
  state: RunStatus,
  action: RunAction,
  currentJob: RunProgress.shape.currentJob,
  counters: RunCounters,
});
export type ProgressUpdateRequest = z.infer<typeof ProgressUpdateRequest>;

export const CompleteRunRequest = z.object({
  status: z.enum([
    'done',
    'needs_attention',
    'failed',
    'cancelled',
    'paused_budget',
    'time_cap_reached',
  ]),
  counters: RunCounters,
  errors: z.array(z.string()).default([]),
  // List of applications the worker started but couldn't finish. The
  // run summary (email + flyout list) surfaces these for manual finish.
  needsCompletionList: z
    .array(
      z.object({
        jobId: z.string(),
        title: z.string(),
        company: z.string(),
        url: z.string().url(),
        reason: z.string(),
      }),
    )
    .default([]),
});
export type CompleteRunRequest = z.infer<typeof CompleteRunRequest>;
